import { defineAsyncComponent, defineComponent, type PropType } from 'vue';
import { mapGetters } from 'vuex';
import route from '@router/route';
import { trackEvent } from '@/modules/tracking';
import JobSeekerMixin from '@mixins/jobSeekerMixin';
import { getXandrAdvertiser } from '@api/backend/dsa';
import store from '@/store';
import { adjustScalling } from '@/modules/xandr';

/**
 * This mixin provides the common logic for xandr, xandrFullScreen and XandrStickyAd functionality
 */
export default defineComponent({
    name: 'XandrMixin',

    extends: JobSeekerMixin,

    props: {
        /**
         * the ad wrapper that will be requested
         */
        xandrWrapper: {
            type: String,
            default: '',
        },
        /**
         * the ad type that will be requested
         */
        xandrSlot: {
            type: String,
            default: '',
        },
        /**
         * The ad sizes that will be requested
         */
        xandrSizes: {
            type: Array as PropType<number[][]>,
            default: () => [],
        },
        /**
         * Whether the ad will be requested on demand.
         */
        adOnDemand: {
            type: Boolean,
            default: false,
        },
        /**
         * This prop will be true only for sponsored header ads
         */
        isSponsoredHeader: {
            type: Boolean,
            default: false,
        },
        /**
         * This prop will be true only for fullscreen ads
         */
        isFullScreenAd: {
            type: Boolean,
            default: false,
        },
        /**
         * This prop will be true only for in feed ads
         */
        isInFeedAd: {
            type: Boolean,
            default: false,
        },
        /**
         * This prop will be true only for dashboard ads
         */
        isDashboardAd: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['ad-clicked'],

    data() {
        return {
            isVisible: false,
            options: {
                threshold: 0.5,
            },
            canShowReportModal: false,
            xandrCreativeData: [],
            isModalOpen: false,
            advertiser: null,
        };
    },

    computed: {
        ...mapGetters({
            dsaReportData: 'dsa/dsaReportData',
            screen: 'ui/screen',
            xandrData: 'xandr/xandrAdData',
            hasAdBlocker: 'ad/hasAdBlocker',
            user: 'auth/user',
            languageCode: 'ui/languageCode',
        }),
        isReported() {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return this.dsaReportData[this.xandrSlot]?.isReported;
        },
        canShowPremiumAds() {
            return this.isInFeedAd || this.isFullScreenAd;
        },
        isPremiumUser() {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return this.user?.is_premium;
        },
        showPremiumAds(): unknown {
            return this.hasAdBlocker && this.canShowPremiumAds && !this.isPremiumUser;
        },
    },

    watch: {
        dsaReportData: {
            deep: true,
            handler(newVal) {
                if (newVal[this.xandrSlot] !== undefined) {
                    this.canShowReportModal = newVal[this.xandrSlot].canShowReportModal;
                    this.xandrCreativeData = newVal[this.xandrSlot].xandrCreativeData;
                }
            },
        },
    },

    methods: {
        trackJobSeekerStatus() {
            this.storeJobSeekerStatus();
        },
        handleAdClick() {
            // since we will handle billboard(also sticky) and fullscreen in the wrappers component
            // when it's a simple banner in the feed we need to also track the img click and redirec user to premium page;
            if (this.xandrSlot.includes('banner') && this.hasAdBlocker) {
                trackEvent('xandr_ads', 'adblock_ad_click', this.xandrData[this.xandrSlot].creative_id);
                window.open(route('subscription.shop.show'), '_blank');
            }

            this.$emit('ad-clicked');
        },
        emitReportAd() {
            if (this.dsaReportData[this.xandrSlot].isReported) return;

            this.isModalOpen = true;
            this.$modal
                .open(
                    defineAsyncComponent(() => import('@/components/ModalReportDsa')),
                    {
                        reportData: this.xandrCreativeData,
                    },
                )
                .then((data) => {
                    this.dsaReportData[this.xandrSlot].isReported = <boolean>data;
                    this.isModalOpen = false;
                });
        },
        adPremiumClick() {
            const trackingName = this.hasAdBlocker ? 'adblock_ad_premium_button_click' : 'xandr_premium_button_click';

            trackEvent('premium_subscription', trackingName, this.xandrSlot);
            window.open(route('subscription.shop.show'), '_blank');
        },
        async openDsaInfo() {
            if (!this.advertiser) {
                await getXandrAdvertiser(
                    this.xandrData[this.xandrSlot].creative_id,
                    this.xandrData[this.xandrSlot].advertiser_id,
                ).then((response) => {
                    this.advertiser = response.data.data;
                });
            }

            this.isModalOpen = true;
            this.$modal
                .open(
                    defineAsyncComponent(() => import('@/components/ModalDsaInfo')),
                    {
                        advertiserData: this.advertiser,
                    },
                )
                .then(() => {
                    this.isModalOpen = false;
                });
        },
        async createFakeXandrData(adType: string) {
            const xandrAdData = {
                adType,
                hasAd: true,
                creative_id: 9999,
                advertiser_id: null,
            };

            // store xandr ad data
            store.dispatch('xandr/setXandrAdData', { ...xandrAdData });

            // Dsa
            const dsaData = {
                ...xandrAdData,
                isReported: false,
                canShowReportModal: true,
                xandrCreativeData: {
                    reported_item_id: 9999,
                    reported_item_type_id: 1,
                    reported_item_placement: adType,
                },
            };

            store.dispatch('dsa/shouldShowReportModal', { ...dsaData });

            return true;
        },
        getPremiumImgPath() {
            switch (true) {
                case this.isInFeedAd:
                    return `/images/premium_ads/${this.languageCode}/billboard_premium.png`;
                case this.isFullScreenAd:
                    return `/images/premium_ads/${this.languageCode}/fullpage_video_premium.png`;
                default:
                    return null;
            }
        },
        loadAndResizeHostImage() {
            if (!this.isFullScreenAd) {
                return;
            }

            adjustScalling(this.xandrSlot);
        },
    },
});
